import React, { useMemo } from 'react';
import { ArrowLeft, FileUp, Link2, Loader2, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Category } from '@/lib/api';
import type { UseOFXReconciliationResult } from './useOFXReconciliation';
import { AmbiguousDecisionPanel } from './AmbiguousDecisionPanel';
import { DuplicatesAccordion } from './DuplicatesAccordion';
import { LinkedEntryRow } from './LinkedEntryRow';
import { NewEntryRow } from './NewEntryRow';

interface ReconciliationBoardProps {
  reconciliation: UseOFXReconciliationResult;
  categories: Category[];
  onCancel: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export const ReconciliationBoard: React.FC<ReconciliationBoardProps> = ({
  reconciliation,
  categories,
  onCancel,
  onSubmit,
  isSubmitting,
}) => {
  const {
    file,
    pairs,
    decisions,
    counts,
    duplicatesOpen,
    rowErrors,
    canSubmit,
    setCategoryId,
    setPickedManualId,
    setAmbiguousCreateNew,
    unlinkDecision,
    revertCreateOverride,
    setDescriptionOverride,
    toggleDuplicates,
  } = reconciliation;

  const categoriesByType = useMemo(
    () => ({
      income: categories.filter((c) => c.type === 'income'),
      expense: categories.filter((c) => c.type === 'expense'),
    }),
    [categories],
  );

  const categoriesById = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.id, c])),
    [categories],
  );

  const groups = useMemo(() => {
    const pending = pairs.filter((p) => decisions[p.fitid]?.intent === 'pending');
    const creates = pairs.filter((p) => decisions[p.fitid]?.intent === 'create');
    const links = pairs.filter((p) => decisions[p.fitid]?.intent === 'link');
    const duplicates = pairs.filter((p) => decisions[p.fitid]?.intent === 'skip');
    return { pending, creates, links, duplicates };
  }, [pairs, decisions]);

  const totalActions = counts.toCreate + counts.toLink;
  const ctaLabel = isSubmitting
    ? 'Importando…'
    : `Confirmar — registrar ${counts.toCreate} · vincular ${counts.toLink}`;

  return (
    <div className="space-y-4 animate-slide-down pb-24">
      {/* Header */}
      <header
        className={cn(
          'relative bg-card border border-border rounded-lg p-5',
          'flex items-start justify-between gap-4',
          'card-accent-primary',
        )}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
            <FileUp className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="font-medium truncate" title={file?.name}>
              {file?.name ?? 'Arquivo OFX'}
            </span>
          </div>
          <h3 className="text-base font-semibold text-foreground">
            {pairs.length} {pairs.length === 1 ? 'transação encontrada' : 'transações encontradas'}
          </h3>
          <CountsBar
            toCreate={counts.toCreate}
            toLink={counts.toLink}
            pending={counts.pending}
            skipped={counts.skipped}
          />
        </div>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 px-2.5 py-1.5 rounded-md transition-colors flex items-center gap-1.5 shrink-0 disabled:opacity-50"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Outro arquivo
        </button>
      </header>

      {/* Decisão necessária */}
      {groups.pending.length > 0 && (
        <AmbiguousDecisionPanel
          rows={groups.pending}
          decisions={decisions}
          categoriesById={categoriesById}
          onPickManual={setPickedManualId}
          onCreateNew={setAmbiguousCreateNew}
        />
      )}

      {/* Two-column main */}
      <section className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 md:divide-x md:divide-border">
          {/* LEFT — Adicionar */}
          <div className="flex flex-col">
            <header className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/20">
              <h4 className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-foreground">
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                Adicionar a Patri
                <span className="text-muted-foreground font-normal">· {counts.toCreate}</span>
              </h4>
            </header>
            <div className="divide-y divide-border/50">
              {groups.creates.length === 0 ? (
                <EmptyZone label="Nenhum lançamento será adicionado" />
              ) : (
                groups.creates.map((pair) => {
                  const decision = decisions[pair.fitid];
                  if (!decision) return null;
                  const isOverride =
                    pair.case === 'auto-link' || pair.case === 'ambiguous';
                  return (
                    <NewEntryRow
                      key={pair.fitid}
                      pair={pair}
                      decision={decision}
                      categoriesByType={categoriesByType}
                      onSetCategoryId={setCategoryId}
                      onSetDescriptionOverride={setDescriptionOverride}
                      onRevertOverride={isOverride ? revertCreateOverride : undefined}
                      errorMessage={rowErrors[pair.fitid]}
                    />
                  );
                })
              )}
            </div>
          </div>

          {/* RIGHT — Vincular */}
          <div className="flex flex-col border-t md:border-t-0 border-border">
            <header className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/20">
              <h4 className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-foreground">
                <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
                Vincular a existentes
                <span className="text-muted-foreground font-normal">· {counts.toLink}</span>
              </h4>
            </header>
            <div className="divide-y divide-border/50">
              {groups.links.length === 0 ? (
                <EmptyZone label="Nenhum lançamento será vinculado" />
              ) : (
                groups.links.map((pair) => {
                  const decision = decisions[pair.fitid];
                  if (!decision) return null;
                  return (
                    <LinkedEntryRow
                      key={pair.fitid}
                      pair={pair}
                      decision={decision}
                      categoriesById={categoriesById}
                      onUnlink={unlinkDecision}
                      errorMessage={rowErrors[pair.fitid]}
                    />
                  );
                })
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Duplicates */}
      <DuplicatesAccordion
        rows={groups.duplicates}
        open={duplicatesOpen}
        onToggle={toggleDuplicates}
      />

      {/* Sticky footer */}
      <div className="sticky bottom-0 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-background/95 backdrop-blur-sm border-t border-border">
        <div className="flex items-center justify-end gap-3">
          {totalActions > 0 && counts.pending === 0 && reconciliation.missingCategoryCount > 0 && (
            <p className="text-xs text-muted-foreground hidden sm:block">
              {reconciliation.missingCategoryCount}{' '}
              {reconciliation.missingCategoryCount === 1
                ? 'lançamento sem categoria'
                : 'lançamentos sem categoria'}
            </p>
          )}
          {counts.pending > 0 && (
            <p className="text-xs text-muted-foreground hidden sm:block">
              {counts.pending} {counts.pending === 1 ? 'decisão pendente' : 'decisões pendentes'}
            </p>
          )}
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="text-sm text-muted-foreground hover:text-foreground hover:bg-muted/40 px-3 py-2 rounded-md transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit || isSubmitting}
            className="flex items-center gap-2 px-4 py-2 rounded-md gradient-primary text-[hsl(220_40%_8%)] text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            <span>{ctaLabel}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

interface CountsBarProps {
  toCreate: number;
  toLink: number;
  pending: number;
  skipped: number;
}

const CountsBar: React.FC<CountsBarProps> = ({ toCreate, toLink, pending, skipped }) => {
  const items: { label: string; value: number; tone: 'default' | 'warn' | 'mute' }[] = [
    { label: 'novas', value: toCreate, tone: 'default' },
    { label: 'vinculadas', value: toLink, tone: 'default' },
  ];
  if (pending > 0) items.push({ label: 'decisões', value: pending, tone: 'warn' });
  if (skipped > 0) items.push({ label: 'já importadas', value: skipped, tone: 'mute' });

  return (
    <p className="mt-1.5 text-xs text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-0.5">
      {items.map((item, i) => (
        <React.Fragment key={item.label}>
          {i > 0 && <span aria-hidden="true">·</span>}
          <span className={cn(item.tone === 'warn' && 'text-foreground font-medium')}>
            <span className="tabular-nums">{item.value}</span> {item.label}
          </span>
        </React.Fragment>
      ))}
    </p>
  );
};

const EmptyZone: React.FC<{ label: string }> = ({ label }) => (
  <div className="px-5 py-10 text-center">
    <p className="text-xs text-muted-foreground/80">{label}</p>
  </div>
);
